import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { petAPI, trainingAPI, aiVetAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { 
  HeartIcon, 
  AcademicCapIcon, 
  ChartBarIcon,
  PlusIcon,
  CalendarIcon,
  TrophyIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const Dashboard = () => {
  const { data: pets, isLoading: petsLoading } = useQuery('pets', petAPI.getPets);

  const petIds = pets?.data?.map(pet => pet.id) || [];
  
  const { data: allStats } = useQuery(
    ['allPetStats', petIds],
    () => Promise.all(petIds.map(id => trainingAPI.getPetStats(id))),
    { enabled: petIds.length > 0 }
  );

  const recentSessions = async () => {
    if (petIds.length === 0) return [];
    const sessionsPromises = petIds.map(id => 
      trainingAPI.getPetSessions(id, { limit: 3 })
    );
    const allSessions = await Promise.all(sessionsPromises);
    const petsData = pets?.data || [];
    return allSessions
      .flatMap(response => response.data.map(session => ({
        ...session,
        petName: petsData.find(pet => pet.id === session.pet_id)?.name
      })))
      .sort((a, b) => new Date(b.started_at) - new Date(a.started_at))
      .slice(0, 5);
  };

  const { data: recentSessionsData } = useQuery(
    ['recentSessions', petIds],
    recentSessions,
    { enabled: petIds.length > 0 }
  );

  const { data: aiVetSessions } = useQuery(
    'aiVetSessions',
    aiVetAPI.getSessions,
    { enabled: petIds.length > 0 }
  );

  if (petsLoading) {
    return <LoadingSpinner text="Loading dashboard..." />;
  }

  const petsData = pets?.data || [];
  const statsData = allStats?.map(stat => stat.data) || [];

  const totalStats = statsData.reduce((acc, stats) => ({
    totalSessions: acc.totalSessions + (parseInt(stats.total_sessions) || 0),
    totalMinutes: acc.totalMinutes + (parseInt(stats.total_training_minutes) || 0),
    totalExercises: acc.totalExercises + (parseInt(stats.exercises_tried) || 0),
    totalMastered: acc.totalMastered + (parseInt(stats.exercises_mastered) || 0),
  }), { totalSessions: 0, totalMinutes: 0, totalExercises: 0, totalMastered: 0 });

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">🏠 Dashboard</h1>
      </div>

      {petsData.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <div className="w-20 h-20 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-full flex items-center justify-center mx-auto animate-bounce-slow">
            <HeartIcon className="h-10 w-10 text-primary-600" />
          </div>
          <h3 className="mt-4 text-lg font-bold text-gray-900">No pets yet</h3>
          <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
            Get started by adding your first pet to begin their training journey!
          </p>
          <div className="mt-6">
            <Link
              to="/app/pets"
              className="inline-flex items-center btn btn-primary"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Your First Pet
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card hover:scale-105 transition-transform duration-300">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center">
                    <HeartIcon className="h-6 w-6 text-primary-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Pets</p>
                  <p className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">{petsData.length}</p>
                </div>
              </div>
            </div>

            <div className="card hover:scale-105 transition-transform duration-300">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-secondary-100 to-secondary-200 rounded-full flex items-center justify-center">
                    <CalendarIcon className="h-6 w-6 text-secondary-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Training Sessions</p>
                  <p className="text-2xl font-bold bg-gradient-to-r from-secondary-600 to-secondary-700 bg-clip-text text-transparent">{totalStats.totalSessions}</p>
                </div>
              </div>
            </div>

            <div className="card hover:scale-105 transition-transform duration-300">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full flex items-center justify-center">
                    <ChartBarIcon className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Training Minutes</p>
                  <p className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-orange-700 bg-clip-text text-transparent">{totalStats.totalMinutes}</p>
                </div>
              </div>
            </div>

            <div className="card hover:scale-105 transition-transform duration-300">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-full flex items-center justify-center">
                    <TrophyIcon className="h-6 w-6 text-yellow-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Exercises Mastered</p>
                  <p className="text-2xl font-bold bg-gradient-to-r from-yellow-600 to-yellow-700 bg-clip-text text-transparent">{totalStats.totalMastered}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pets Overview */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">🐾 My Pets</h2>
              <Link to="/app/pets" className="btn btn-outline btn-sm">
                View All
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {petsData.slice(0, 3).map((pet) => {
                const petStats = statsData.find((_, index) => petIds[index] === pet.id) || {};
                return (
                  <Link
                    key={pet.id}
                    to={`/app/pets/${pet.id}`}
                    className="p-4 border-2 border-gray-200 rounded-xl hover:border-primary-300 hover:shadow-lg transition-all duration-300 hover:scale-105 bg-gradient-to-br from-white to-primary-50"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-full flex items-center justify-center animate-bounce-slow">
                        <span className="text-2xl">🐕</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900">{pet.name}</h3>
                        <p className="text-sm text-gray-500 capitalize">{pet.pet_type}</p>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                      <div className="text-center p-2 bg-blue-50 rounded-lg">
                        <p className="text-gray-500 text-xs">Sessions</p>
                        <p className="font-bold text-blue-600">{petStats.total_sessions || 0}</p>
                      </div>
                      <div className="text-center p-2 bg-green-50 rounded-lg">
                        <p className="text-gray-500 text-xs">Mastered</p>
                        <p className="font-bold text-green-600">{petStats.exercises_mastered || 0}</p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Recent AI Vet Chats */}
          {aiVetSessions?.data && aiVetSessions.data.length > 0 && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">💬 Recent AI Vet Chats</h2>
                <Link to="/app/virtual-vet" className="btn btn-outline btn-sm">
                  View All
                </Link>
              </div>
              <div className="space-y-3">
                {aiVetSessions.data.slice(0, 3).map((session) => (
                  <Link
                    key={session.id}
                    to={`/app/virtual-vet/chat/${session.id}`}
                    className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 rounded-lg px-3"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                        <ChatBubbleLeftRightIcon className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{session.title}</p>
                        <p className="text-sm text-gray-500">
                          {session.pet_name} • {session.message_count} messages
                        </p>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {session.last_message_at && new Date(session.last_message_at).toLocaleDateString()}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Recent Activity */}
          {recentSessionsData && recentSessionsData.length > 0 && (
            <div className="card">
              <h2 className="text-xl font-bold bg-gradient-to-r from-secondary-600 to-primary-600 bg-clip-text text-transparent mb-4">🏃 Recent Training Sessions</h2>
              <div className="space-y-3">
                {recentSessionsData.map((session) => (
                  <div key={session.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center">
                        <AcademicCapIcon className="w-4 h-4 text-secondary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {session.petName} - {session.exercise_name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {new Date(session.started_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <div
                            key={i}
                            className={`w-2 h-2 rounded-full ${
                              i < session.success_rating ? 'bg-yellow-400' : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-gray-500">
                        {session.duration_minutes}min
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="card">
            <h2 className="text-xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent mb-4">⚡ Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link
                to="/app/pets"
                className="p-4 border-2 border-gray-200 rounded-xl hover:border-primary-300 hover:shadow-lg transition-all text-center bg-gradient-to-br from-white to-primary-50 hover:scale-105"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mx-auto mb-3">
                  <PlusIcon className="w-8 h-8 text-primary-600" />
                </div>
                <p className="font-medium text-gray-900">Add New Pet</p>
                <p className="text-sm text-gray-500">Register a new pet for training</p>
              </Link>
              
              <Link
                to="/app/exercises"
                className="p-4 border-2 border-gray-200 rounded-xl hover:border-secondary-300 hover:shadow-lg transition-all text-center bg-gradient-to-br from-white to-secondary-50 hover:scale-105"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-secondary-100 to-secondary-200 rounded-full flex items-center justify-center mx-auto mb-3">
                  <AcademicCapIcon className="w-8 h-8 text-secondary-600" />
                </div>
                <p className="font-medium text-gray-900">Browse Exercises</p>
                <p className="text-sm text-gray-500">Find new training exercises</p>
              </Link>
              
              <Link
                to="/app/programs"
                className="p-4 border-2 border-gray-200 rounded-xl hover:border-orange-300 hover:shadow-lg transition-all text-center bg-gradient-to-br from-white to-orange-50 hover:scale-105"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full flex items-center justify-center mx-auto mb-3">
                  <ChartBarIcon className="w-8 h-8 text-orange-600" />
                </div>
                <p className="font-medium text-gray-900">Training Programs</p>
                <p className="text-sm text-gray-500">Follow structured training plans</p>
              </Link>

              <Link
                to="/app/virtual-vet"
                className="p-4 border-2 border-gray-200 rounded-xl hover:border-purple-300 hover:shadow-lg transition-all text-center bg-gradient-to-br from-white to-purple-50 hover:scale-105"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center mx-auto mb-3">
                  <ChatBubbleLeftRightIcon className="w-8 h-8 text-purple-600" />
                </div>
                <p className="font-medium text-gray-900">Virtual Vet</p>
                <p className="text-sm text-gray-500">Consult with licensed veterinarians</p>
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;